<template>
  <div class="flex flex-col gap-3">
    <div
      v-if="!fileName"
      class="file-container"
      @drop.prevent="handleFileSelect($event, 'drop')"
      @dragover.prevent
      @dragleave.prevent
      @dragenter.prevent
    >
      <label for="file" class="flex gap-4 flex-col cursor-pointer items-center justify-center">
        <icon name="new_card" :size="40" color="#C2C3C4" />
        <p class="text-[12px] font-normal text-[#C2C3C4]">
          Drag and drop file or click to browse files
        </p>
        <input
          ref="fileInput"
          type="file"
          accept=".json"
          @change="handleFileSelect"
          style="display: none"
          id="file"
        />
      </label>
    </div>
    <div v-else class="flex items-center justify-between">
      <p>{{ fileName }}</p>
      <button v-if="fileName" @click="clearFile" class="clear-button">
        <icon name="x" :size="14" color="#e86363" />
      </button>
    </div>
    <div v-if="configPreview && !error" class="config-preview">
      <div class="flex items-center justify-between mb-3">
        <span class="text-[#e4e4e4] text-xs flex-1 font-semibold">Configuration Preview:</span>
        <button @click="showFullConfig = !showFullConfig" class="toggle-btn">
          {{ showFullConfig ? 'Hide Details' : 'Show Details' }}
        </button>
      </div>

      <div class="preview-content">
        <div class="config-summary">
          <div class="summary-item">
            <span class="label">Device Name:</span>
            <span class="value">{{ configPreview.deviceName || 'Not specified' }}</span>
          </div>
          <div class="summary-item">
            <span class="label">Organisation ID:</span>
            <span class="value">{{ configPreview.organisationID || 'Not specified' }}</span>
          </div>
          <div v-if="isDJIConfig" class="summary-item">
            <span class="label">Simulation Location:</span>
            <span class="value">{{ configPreview.simLat }}, {{ configPreview.simLon }}</span>
          </div>
        </div>

        <div v-if="showFullConfig" class="full-config">
          <pre class="config-json">{{ JSON.stringify(configPreview, null, 2) }}</pre>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed } from 'vue'
import Icon from './Icon.vue'
import DevicePairing from '@/composables/Device/DevicePairing'

interface Props {
  deviceType?: 'DJI_RC_Plus' | 'DJI_RC_Pro' | 'IDI_DOCK_S'
}

const props = withDefaults(defineProps<Props>(), {
  deviceType: 'DJI_RC_Plus',
})

const emits = defineEmits<{
  configImported: [config: any]
  configCleared: []
  error: [message: string]
}>()

const { validateImportedConfig } = DevicePairing()

const fileInput = ref<HTMLInputElement>()
const fileName = ref<string>('')
const configPreview = ref<any>(null)
const error = ref<string>('')
const showFullConfig = ref(false)
const isValidating = ref(false)

const isDJIConfig = computed(() => {
  return props.deviceType === 'DJI_RC_Plus' || props.deviceType === 'DJI_RC_Pro'
})

const handleFileSelect = async (event: Event | DragEvent, method?: 'drop') => {
  let file: File | undefined
  if (method === 'drop') {
    event.preventDefault()
    const dragEvent = event as DragEvent
    file = dragEvent.dataTransfer?.files?.[0]
  } else {
    const input = event.target as HTMLInputElement | null
    file = input?.files?.[0]
  }
  if (!file) return
  await processFile(file)
}
const MAX_FILE_SIZE = 5 * 1024 * 1024
const processFile = async (file: File) => {
  isValidating.value = true

  if (!file.name.endsWith('.json')) {
    setError('Please select a valid JSON file')
    isValidating.value = false
    return
  }

  if (file.size > MAX_FILE_SIZE) {
    setError('File size exceeds the 5MB limit. Please select a smaller file.')
    isValidating.value = false
    return
  }

  try {
    const text = await file.text()
    const config = JSON.parse(text)

    if (!config || typeof config !== 'object') {
      setError('Invalid JSON format')
      return
    }

    const validationResult = await validateImportedConfig(config, props.deviceType)
    if (!validationResult) {
      setError(
        'Configuration missing required fields. Please ensure your config contains all default fields.',
      )
      return
    }

    fileName.value = file.name
    configPreview.value = config

    emits('configImported', config)
  } catch (err) {
    if (err instanceof SyntaxError) {
      setError('Invalid JSON format. Please check your file syntax.')
    } else {
      setError('Failed to validate configuration. Please check the file format.')
    }
  } finally {
    isValidating.value = false
  }
}
const clearFile = () => {
  if (fileInput.value) {
    fileInput.value.value = ''
  }
  fileName.value = ''
  configPreview.value = null
  showFullConfig.value = false

  emits('configCleared')
}

const setError = (message: string) => {
  fileName.value = ''
  configPreview.value = null

  emits('error', message)
}
</script>

<style scoped>
.config-preview {
  background-color: var(--idi-shade1-50);
  border: 1px solid var(--idi-shade3-50);
  border-radius: 8px;
  padding: 12px;
}

.toggle-btn {
  background: transparent;
  border: 1px solid var(--idi-shade3-50);
  color: #95989c;
  border-radius: 4px;
  padding: 4px 8px;
  font-size: 10px;
  cursor: pointer;
  transition: opacity 0.2s ease;
}

.toggle-btn:hover {
  opacity: 0.8;
}

.preview-content {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.config-summary {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.summary-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.label {
  color: #95989c;
  font-size: 11px;
  font-weight: 400;
}

.value {
  color: #e4e4e4;
  font-size: 11px;
  font-weight: 500;
  max-width: 60%;
  text-align: right;
  word-break: break-all;
}

.full-config {
  margin-top: 8px;
}

.config-json {
  background-color: var(--idi-shade0-50);
  border-radius: 4px;
  padding: 8px;
  font-family: 'Courier New', monospace;
  font-size: 10px;
  color: #c2c3c4;
  overflow-x: auto;
  max-height: 200px;
  overflow-y: auto;
}
</style>
